import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { updateContact, getContactById, deleteContact } from '../api/contacts'; // added deleteContact import
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const initialState = {
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  relationship: '',
  phone: '',
  company: '',
  property: '',
  notes: '',
};

const relationships = [
  { value: '', label: 'Select relationship' },
  { value: 'legal', label: 'Legal' },
  { value: 'agent', label: 'Agent' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' },
];

function EditContact() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const { contact, refetch } = location.state || {};

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const { run: runSubmission, isPending } = useSubmissionLock();

  useEffect(() => {
    if (contact) {
      console.log("Loaded contact into edit form:", contact);
      setForm({
        id: contact.id || routeId || '',
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        relationship: contact.relationship || '',
        phone: contact.phone || '',
        company: contact.company || '',
        property: contact.property || '',
        notes: contact.notes || '',
      });
    } else if (routeId) {
      getContactById(routeId)
        .then((res) => {
          const fetched = res.data;
          setForm({
            id: fetched.id,
            firstName: fetched.firstName || '',
            lastName: fetched.lastName || '',
            email: fetched.email || '',
            relationship: fetched.relationship || '',
            phone: fetched.phone || '',
            company: fetched.company || '',
            property: fetched.property || '',
            notes: fetched.notes || '',
          });
        })
        .catch((err) => {
          console.error('Failed to fetch contact by ID:', err);
          alert('Could not load contact.');
          navigate('/contacts');
        });
    }
  }, [contact, routeId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || isPending(`contact:${form.id}:save`)) return;
    console.log('Updating contact with ID:', form.id);
    await runSubmission(`contact:${form.id}:save`, async () => {
      setSaving(true);
      try {
        await updateContact(form.id, form);
        if (refetch) await refetch(); // Refresh contact list if passed
        navigate('/contacts');
      } catch (err) {
        console.error('Failed to update contact:', err);
        alert('Failed to update contact');
      } finally {
        setSaving(false);
      }
    });
  };

  // New Delete handler
  const handleDelete = async () => {
    if (saving || isPending(`contact:${form.id}:delete`)) return;

    await runSubmission(`contact:${form.id}:delete`, async () => {
      if (!window.confirm('Are you sure you want to delete this contact?')) return;

      setSaving(true);
      try {
        await deleteContact(form.id);
        if (refetch) await refetch();
        navigate('/contacts');
      } catch (err) {
        console.error('Failed to delete contact:', err);
        alert('Failed to delete contact');
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <div className="max-w-2xl mx-auto mt-10 bg-white/10 rounded-xl border border-white/10 p-8 backdrop-blur-md text-white">
      <button
        className="flex items-center gap-2 mb-6 text-white hover:text-[#AF9B8A] transition"
        onClick={() => navigate(-1)}
        type="button"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>
      <h2 className="text-2xl font-bold mb-6">Edit Contact</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Your existing form inputs */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block mb-1 font-semibold">First Name</label>
            <input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
              required
            />
          </div>
          <div className="flex-1">
            <label className="block mb-1 font-semibold">Last Name</label>
            <input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
              required
            />
          </div>
        </div>

        <div>
          <label className="block mb-1 font-semibold">Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            required
          />
        </div>

        <div className="flex gap-4 relative">
          <div className="flex-1 relative">
            <label className="block mb-1 font-semibold">Relationship</label>
            <select
              name="relationship"
              value={form.relationship}
              onChange={handleChange}
              required
              className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/5 border border-white/10"
            >
              {relationships.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-9 pointer-events-none w-4 h-4 text-white/70" />
          </div>
          <div className="flex-1">
            <label className="block mb-1 font-semibold">Phone</label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
            />
          </div>
        </div>

        <div>
          <label className="block mb-1 font-semibold">Company</label>
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={handleChange}
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block mb-1 font-semibold">Notes</label>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 bg-[#AF9B8A] rounded-xl text-white font-semibold hover:bg-[#9b897a] transition"
        >
          {isPending(`contact:${form.id}:save`) ? 'Saving...' : 'Save Contact'}
        </button>

        {/* Delete button */}
        <button
          type="button"
          onClick={handleDelete}
          disabled={saving}
          className="w-full py-3 mt-2 bg-red-600 rounded-xl text-white font-semibold hover:bg-red-700 transition"
        >
          {isPending(`contact:${form.id}:delete`) ? 'Deleting...' : 'Delete Contact'}
        </button>
      </form>
    </div>
  );
}

export default EditContact;
